import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { navPaths } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLang();

  const links = [
    { label: t.nav.home, to: navPaths.home },
    { label: t.nav.about, to: navPaths.about },
    { label: t.nav.innovation, to: navPaths.innovation },
    { label: t.nav.whatWeDo, to: navPaths.whatWeDo },
    { label: t.nav.recipes, to: navPaths.recipes },
    { label: t.nav.allf, to: navPaths.allf },
    { label: t.nav.contact, to: navPaths.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || open;

  const LangSwitch = ({ className = "" }) => (
    <div className={`flex items-center rounded-full border ${solid ? "border-black/15" : "border-white/30"} overflow-hidden ${className}`}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-xs font-bold uppercase transition-colors ${
            lang === l
              ? "bg-[#17784A] text-white"
              : solid ? "text-[#3B342E] hover:bg-black/5" : "text-white/80 hover:bg-white/10"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-[#FBF9F4]/90 backdrop-blur-md shadow-[0_2px_20px_rgba(20,17,15,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="tricolor-bar h-1 w-full" />
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-1.5 group">
          <span className="inline-flex items-center bg-white rounded-xl px-2.5 py-1.5 shadow-sm border border-black/5 group-hover:shadow-md transition-shadow">
            <img src="/logo-mc.png" alt="MichelChef" className="h-8 w-auto" />
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-7">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`link-underline text-[15px] font-medium transition-colors ${
                  solid ? "text-[#3B342E] hover:text-[#17784A]" : "text-white/90 hover:text-white"
                } ${active ? "!text-[#17784A]" : ""}`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <LangSwitch className="hidden sm:flex" />
          <button
            className={`xl:hidden p-2 rounded-lg ${solid ? "text-[#14110F]" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <div
        className={`xl:hidden overflow-hidden transition-all duration-400 bg-[#FBF9F4] ${
          open ? "max-h-[560px] border-t border-black/5" : "max-h-0"
        }`}
      >
        <div className="px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`py-3 px-3 rounded-lg text-[16px] font-medium ${
                location.pathname === l.to ? "bg-[#17784A]/10 text-[#17784A]" : "text-[#3B342E]"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center mt-3">
            <LangSwitch />
          </div>
        </div>
      </div>
    </header>
  );
}
